import { Injectable } from '@angular/core';
import { NotificationItem } from '@shared/models/notification-item';
import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app';
import { getMessaging, getToken, isSupported, MessagePayload, Messaging, onMessage } from 'firebase/messaging';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';

const SW_FETCH_MESSAGE = 'BUENO_FCM_FETCH';
const SW_BACKGROUND_MESSAGE = 'BUENO_FCM_BACKGROUND_MESSAGE';
const SW_SYNC_MESSAGE = 'BUENO_FCM_SYNC';

type ServiceWorkerMessageData = {
  type?: string;
  notification?: Partial<NotificationItem>;
  notifications?: Partial<NotificationItem>[];
};

@Injectable({
  providedIn: 'root',
})
export class FirebaseNotificationsService {
  private readonly storageKey = 'bueno.notifications';
  private readonly tokenStorageKey = 'bueno.notifications.token';
  private readonly maxNotifications = 20;
  private readonly notificationsSubject = new BehaviorSubject<NotificationItem[]>(this.loadNotifications());
  private readonly unreadCountSubject = new BehaviorSubject<number>(this.countUnread(this.notificationsSubject.value));
  private readonly permissionSubject = new BehaviorSubject<NotificationPermission>(this.getPermission());
  private readonly tokenSubject = new BehaviorSubject<string | null>(this.getStoredToken());
  private initializationPromise?: Promise<boolean>;
  private app?: FirebaseApp;
  private messaging?: Messaging;
  private serviceWorkerRegistration?: ServiceWorkerRegistration;
  private listening = false;
  private serviceWorkerListening = false;
  private serviceWorkerSyncRequested = false;

  readonly notifications$ = this.notificationsSubject.asObservable();
  readonly unreadCount$ = this.unreadCountSubject.asObservable();
  readonly permission$ = this.permissionSubject.asObservable();
  readonly token$ = this.tokenSubject.asObservable();

  initialize(): void {
    if (!this.initializationPromise) {
      this.initializationPromise = this.setupMessaging();
    }
  }

  async requestPermission(): Promise<NotificationPermission> {
    if (!this.canUseNotificationApi()) {
      return 'denied';
    }

    const permission = await Notification.requestPermission();
    this.permissionSubject.next(permission);

    if (permission === 'granted') {
      const initialized = await this.ensureInitialized();
      if (initialized) {
        await this.fetchToken();
      }
    }

    return permission;
  }

  markAllRead(): void {
    const updated = this.notificationsSubject.value.map(notification => ({
      ...notification,
      read: true,
    }));
    this.updateNotifications(updated);
  }

  markAsRead(id: string): void {
    const updated = this.notificationsSubject.value.map(notification =>
      notification.id === id ? { ...notification, read: true } : notification
    );
    this.updateNotifications(updated);
  }

  removeAllNotifications(): void {
    this.updateNotifications([]);
  }

  removeNotification(id: string): void {
    const updated = this.notificationsSubject.value.filter(notification => notification.id !== id);
    this.updateNotifications(updated);
  }

  private async setupMessaging(): Promise<boolean> {
    if (!this.canUseNotificationApi() || !this.hasFirebaseConfig()) {
      return false;
    }

    try {
      const supported = await isSupported();
      if (!supported) {
        return false;
      }

      this.app = this.getFirebaseApp();
      this.messaging = getMessaging(this.app);
      this.listenForForegroundMessages();
      this.listenForServiceWorkerMessages();
      await this.registerServiceWorker();
      void this.requestStoredNotifications();

      if (this.permissionSubject.value === 'granted') {
        await this.fetchToken();
      }

      return true;
    } catch (error) {
      console.error('[FirebaseNotifications] Initialization failed', error);
      return false;
    }
  }

  private async ensureInitialized(): Promise<boolean> {
    if (!this.initializationPromise) {
      this.initialize();
    }

    return (await this.initializationPromise) ?? false;
  }

  private getFirebaseApp(): FirebaseApp {
    if (this.app) {
      return this.app;
    }

    const apps = getApps();
    this.app = apps.length > 0 ? getApp() : initializeApp(environment.firebase);
    return this.app;
  }

  private async registerServiceWorker(): Promise<void> {
    if (!this.isBrowser() || !('serviceWorker' in navigator) || this.serviceWorkerRegistration) {
      return;
    }

    try {
      this.serviceWorkerRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    } catch (error) {
      console.error('[FirebaseNotifications] Service worker registration failed', error);
    }
  }

  private async requestStoredNotifications(): Promise<void> {
    if (!this.isBrowser() || !('serviceWorker' in navigator) || this.serviceWorkerSyncRequested) {
      return;
    }

    const message = { type: SW_FETCH_MESSAGE };
    this.serviceWorkerSyncRequested = true;

    try {
      const controller = navigator.serviceWorker.controller;
      if (controller) {
        controller.postMessage(message);
        return;
      }

      const registration = this.serviceWorkerRegistration ?? (await navigator.serviceWorker.getRegistration());
      if (registration?.active) {
        registration.active.postMessage(message);
        return;
      }

      this.serviceWorkerSyncRequested = false;
    } catch (error) {
      this.serviceWorkerSyncRequested = false;
      console.error('[FirebaseNotifications] Failed to request service worker sync', error);
    }
  }

  private async fetchToken(): Promise<void> {
    if (!this.messaging || !this.isBrowser()) {
      return;
    }

    const vapidKey = environment.firebase?.vapidKey;
    if (!vapidKey) {
      return;
    }

    try {
      const token = await getToken(this.messaging, {
        vapidKey,
        serviceWorkerRegistration: this.serviceWorkerRegistration,
      });
      if (token) {
        this.tokenSubject.next(token);
        this.persistToken(token);
      }
    } catch (error) {
      console.error('[FirebaseNotifications] Unable to retrieve token', error);
    }
  }

  private listenForForegroundMessages(): void {
    if (!this.messaging || this.listening) {
      return;
    }

    this.listening = true;
    onMessage(this.messaging, payload => this.handleForegroundMessage(payload));
  }

  private handleForegroundMessage(payload: MessagePayload): void {
    const notification = this.mapPayload(payload);
    this.addNotification(notification);
  }

  private listenForServiceWorkerMessages(): void {
    if (!this.isBrowser() || !('serviceWorker' in navigator) || this.serviceWorkerListening) {
      return;
    }

    this.serviceWorkerListening = true;
    navigator.serviceWorker.addEventListener('message', event => {
      this.handleServiceWorkerMessage(event.data);
    });
  }

  private handleServiceWorkerMessage(data: unknown): void {
    if (!data || typeof data !== 'object') {
      return;
    }

    const message = data as ServiceWorkerMessageData;

    switch (message.type) {
      case SW_BACKGROUND_MESSAGE: {
        if (!message.notification) {
          return;
        }

        const notification = this.normalizeNotification(message.notification);
        if (notification) {
          this.addNotification(notification);
        }
        return;
      }
      case SW_SYNC_MESSAGE: {
        if (!Array.isArray(message.notifications)) {
          return;
        }

        const notifications = message.notifications
          .map(item => this.normalizeNotification(item))
          .filter((item): item is NotificationItem => Boolean(item));

        if (notifications.length > 0) {
          notifications.sort((a, b) => a.receivedAt - b.receivedAt);
          notifications.forEach(notification => this.addNotification(notification));
        }
        return;
      }
      default:
        return;
    }
  }

  private mapPayload(payload: MessagePayload): NotificationItem {
    const data = payload.data ?? {};
    const receivedAt = this.parseTimestamp(data['timestamp']);
    const title = payload.notification?.title ?? data['title'] ?? 'Notification';
    const body = payload.notification?.body ?? data['body'] ?? '';
    const actionUrl = data['url'] ?? data['link'];
    const imageUrl = payload.notification?.image ?? data['image'];
    const id = data['id'] ?? this.createId();

    return {
      id,
      title,
      body,
      receivedAt,
      read: false,
      actionUrl: actionUrl || undefined,
      imageUrl: imageUrl || undefined,
    };
  }

  private parseTimestamp(value?: string): number {
    const parsed = value ? Number(value) : NaN;
    return Number.isFinite(parsed) ? parsed : Date.now();
  }

  private normalizeNotification(value?: Partial<NotificationItem> | null): NotificationItem | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const id = typeof value.id === 'string' && value.id ? value.id : this.createId();
    const title = typeof value.title === 'string' && value.title ? value.title : 'Notification';
    const body = typeof value.body === 'string' ? value.body : '';
    const receivedAt =
      typeof value.receivedAt === 'number' && Number.isFinite(value.receivedAt) ? value.receivedAt : Date.now();
    const actionUrl = typeof value.actionUrl === 'string' && value.actionUrl ? value.actionUrl : undefined;
    const imageUrl = typeof value.imageUrl === 'string' && value.imageUrl ? value.imageUrl : undefined;
    const read = typeof value.read === 'boolean' ? value.read : false;

    return {
      id,
      title,
      body,
      receivedAt,
      read,
      actionUrl,
      imageUrl,
    };
  }

  private addNotification(notification: NotificationItem): void {
    const existing = this.notificationsSubject.value;
    const withoutDuplicate = existing.filter(item => item.id !== notification.id);
    const updated = [notification, ...withoutDuplicate].slice(0, this.maxNotifications);
    this.updateNotifications(updated);
  }

  private updateNotifications(notifications: NotificationItem[]): void {
    this.notificationsSubject.next(notifications);
    this.unreadCountSubject.next(this.countUnread(notifications));
    this.persistNotifications(notifications);
  }

  private countUnread(notifications: NotificationItem[]): number {
    return notifications.filter(notification => !notification.read).length;
  }

  private hasFirebaseConfig(): boolean {
    const config = environment.firebase;
    return Boolean(config?.apiKey && config?.messagingSenderId && config?.projectId && config?.appId);
  }

  private loadNotifications(): NotificationItem[] {
    if (!this.isBrowser()) {
      return [];
    }

    try {
      const raw = localStorage.getItem(this.storageKey);
      const parsed = raw ? (JSON.parse(raw) as Partial<NotificationItem>[]) : [];
      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed
        .map(item => this.normalizeNotification(item))
        .filter((item): item is NotificationItem => Boolean(item))
        .sort((a, b) => b.receivedAt - a.receivedAt)
        .slice(0, this.maxNotifications);
    } catch (error) {
      console.error('[FirebaseNotifications] Failed to read stored notifications', error);
      return [];
    }
  }

  private persistNotifications(notifications: NotificationItem[]): void {
    if (!this.isBrowser()) {
      return;
    }

    try {
      localStorage.setItem(this.storageKey, JSON.stringify(notifications));
    } catch (error) {
      console.error('[FirebaseNotifications] Failed to persist notifications', error);
    }
  }

  private getPermission(): NotificationPermission {
    if (!this.canUseNotificationApi()) {
      return 'default';
    }

    return Notification.permission;
  }

  private getStoredToken(): string | null {
    if (!this.isBrowser()) {
      return null;
    }

    try {
      return localStorage.getItem(this.tokenStorageKey);
    } catch {
      return null;
    }
  }

  private persistToken(token: string): void {
    if (!this.isBrowser()) {
      return;
    }

    try {
      localStorage.setItem(this.tokenStorageKey, token);
    } catch (error) {
      console.error('[FirebaseNotifications] Failed to persist token', error);
    }
  }

  private createId(): string {
    if (this.isBrowser() && 'crypto' in window && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  private isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof navigator !== 'undefined';
  }

  private canUseNotificationApi(): boolean {
    return this.isBrowser() && 'Notification' in window;
  }
}
